import React from "react";
import { checkTranslation, imageAddress } from '../../utils/useful';
import CustomDropZone from "../CustomDropZone.jsx";

const IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'];
const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];

function getFileKind(fileOrMeta, previewUrl = null) {
  const isOldMeta = fileOrMeta && typeof fileOrMeta === 'object' && !(fileOrMeta instanceof File);
  if (isOldMeta) {
    const ext = (fileOrMeta.extension || '').toLowerCase().replace(/^\./, '');
    const mime = (fileOrMeta.mime || '').toLowerCase();
    const isImage = IMAGE_EXT.includes(ext) || mime.startsWith('image/');
    if (isImage) return { isImage: true, kind: 'image', ext, name: fileOrMeta.originalName || fileOrMeta.address || 'file' };
    if (ext === 'pdf' || mime.includes('pdf')) return { isImage: false, kind: 'pdf', ext: 'pdf', name: fileOrMeta.originalName || fileOrMeta.address || 'document.pdf' };
    if (['xls', 'xlsx', 'csv'].includes(ext) || mime.includes('spreadsheet') || mime.includes('excel')) return { isImage: false, kind: 'excel', ext, name: fileOrMeta.originalName || fileOrMeta.address || 'sheet' };
    if (['doc', 'docx'].includes(ext) || mime.includes('msword') || mime.includes('word')) return { isImage: false, kind: 'word', ext, name: fileOrMeta.originalName || fileOrMeta.address || 'document' };
    return { isImage: false, kind: 'generic', ext: ext || 'file', name: fileOrMeta.originalName || fileOrMeta.address || 'file' };
  }
  if (fileOrMeta instanceof File) {
    const name = fileOrMeta.name || '';
    const ext = name.split('.').pop()?.toLowerCase() || '';
    const type = (fileOrMeta.type || '').toLowerCase();
    const isImage = type.startsWith('image/') || IMAGE_EXT.includes(ext);
    if (isImage) return { isImage: true, kind: 'image', ext, name, previewUrl };
    if (ext === 'pdf' || type.includes('pdf')) return { isImage: false, kind: 'pdf', ext: 'pdf', name };
    if (['xls', 'xlsx', 'csv'].includes(ext) || type.includes('spreadsheet') || type.includes('excel')) return { isImage: false, kind: 'excel', ext, name };
    if (['doc', 'docx'].includes(ext) || type.includes('msword') || type.includes('word')) return { isImage: false, kind: 'word', ext, name };
    return { isImage: false, kind: 'generic', ext: ext || 'file', name };
  }
  return { isImage: false, kind: 'generic', ext: 'file', name: 'file' };
}

function FileTypeIcon({ kind, ext, size = 56 }) {
  const s = size;
  const common = { width: s, height: s, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: kind === 'generic' ? 10 : 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase' };
  if (kind === 'pdf') return <div style={{ ...common, background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)', boxShadow: '0 2px 8px rgba(229,57,53,0.4)' }}>PDF</div>;
  if (kind === 'excel') return <div style={{ ...common, background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)', boxShadow: '0 2px 8px rgba(46,125,50,0.4)' }}>{ext === 'csv' ? 'CSV' : 'XLS'}</div>;
  if (kind === 'word') return <div style={{ ...common, background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)', boxShadow: '0 2px 8px rgba(21,101,192,0.4)' }}>DOC</div>;
  return <div style={{ ...common, background: 'linear-gradient(135deg, #546e7a 0%, #37474f 100%)', boxShadow: '0 2px 8px rgba(84,110,122,0.3)' }}>{ext.slice(0, 3)}</div>;
}

class File extends React.Component {
  state = {
    images: [],
    files: [],
    oldFiles: null
  };

  componentDidMount() {
    if (this.props.data) {
      const data = JSON.parse(JSON.stringify(this.props.data));
      this.setState({ oldFiles: Array.isArray(data) ? data : [data] });
    } else {
      this.setState({ oldFiles: null });
    }
    if (this.props.files) {
      const files = Array.isArray(this.props.files) ? [...this.props.files] : [this.props.files];
      const urls = files.map(f => URL.createObjectURL(f));
      this.setState({ images: urls, files });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      const data = this.props.data;
      if (data && data !== '' && JSON.stringify(data) !== '[]') {
        const copy = JSON.parse(JSON.stringify(data));
        this.setState({ oldFiles: Array.isArray(copy) ? copy : [copy] });
      } else {
        this.setState({ oldFiles: [] });
      }
    }
  }

  componentWillUnmount() {
    this.state.images.forEach(url => {
      if (typeof url === 'string' && url.startsWith('blob:')) URL.revokeObjectURL(url);
    });
  }

  chooseFile = (newFiles, newImages) => {
    let images = this.state.images;
    let files = this.state.files;
    const info = this.props.header?.information;

    if (info?.single) {
      this.state.images.forEach(u => { if (typeof u === 'string' && u.startsWith('blob:')) URL.revokeObjectURL(u); });
      images = (Array.isArray(newFiles) ? newFiles : [newFiles]).map(f => URL.createObjectURL(f));
      files = Array.isArray(newFiles) ? newFiles[0] : newFiles;
    } else {
      if (!Array.isArray(images)) images = [];
      if (!Array.isArray(files)) files = [];
      const toAdd = Array.isArray(newFiles) ? newFiles : [newFiles];
      images = [...images, ...toAdd.map(f => URL.createObjectURL(f))];
      files = [...files, ...toAdd];
    }

    this.setState({ images, files });
    this.props.changeFiles(files, this.props.header?.key, this.props.extra);
  };

  removeOldFile = (index) => {
    const oldFiles = [...(this.state.oldFiles || [])];
    oldFiles.splice(index, 1);
    this.setState({ oldFiles });
    this.props.onChange(this.props.header.key, oldFiles, this.props.extra);
  };

  removeFile = (key, index) => {
    const images = [...this.state.images];
    const files = [...(Array.isArray(this.state.files) ? this.state.files : [this.state.files].filter(Boolean))];
    if (images[index] && typeof images[index] === 'string' && images[index].startsWith('blob:')) {
      URL.revokeObjectURL(images[index]);
    }
    images.splice(index, 1);
    files.splice(index, 1);
    this.setState({ images, files: files.length ? files : null });
    this.props.changeFiles(files.length ? files : null, this.props.header?.key, this.props.extra);
  };

  render() {
    const info = this.props.header?.information || {};
    const single = info.single && (this.state.images?.length > 0 || (this.state.oldFiles?.length > 0));

    return (
      <div className="text-center" style={{ width: '100%', textAlign: 'center', height: info.singleRow ? '28px' : 'auto' }}>
        {!single && (
          <CustomDropZone information={info} onDrop={this.chooseFile}>
            <div
              className="pt-3 pb-3 px-3"
              style={{
                width: '100%',
                textAlign: 'center',
                border: '2px dashed #cbd5e1',
                borderRadius: 12,
                backgroundColor: '#f8fafc',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = '#007aff';
                e.currentTarget.style.backgroundColor = '#eff6ff';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
            >
              <img src="/images/submit.svg" alt="" width={48} height={48} style={{ opacity: 0.9 }} />
              <p style={{ marginTop: 8, fontSize: 12, color: '#007aff', fontWeight: 600 }}>
                {checkTranslation(info.single ? '{{lang}}Choose file' : '{{lang}}Choose files')}
              </p>
              <p style={{ marginTop: 2, fontSize: 11, color: '#64748b' }}>
                {checkTranslation('{{lang}}Drag and drop or click to upload')}
              </p>
            </div>
          </CustomDropZone>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12, justifyContent: 'center' }}>
          {this.state.oldFiles && Array.isArray(this.state.oldFiles) && this.state.oldFiles.map((prop, index) => {
            const url = imageAddress(prop);
            const meta = getFileKind(prop);
            const displayName = meta.name && meta.name.length > 30 ? meta.name.slice(0, 27) + '…' : (meta.name || 'file');
            return (
              <div
                key={`old-${index}`}
                style={{
                  position: 'relative',
                  width: 100,
                  flexShrink: 0
                }}
              >
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                  title={checkTranslation('{{lang}}View file')}
                >
                  <div
                    style={{
                      borderRadius: 12,
                      overflow: 'hidden',
                      border: '1px solid #e2e8f0',
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                    }}
                  >
                    {meta.isImage ? (
                      <div style={{ width: 100, height: 100, background: '#f1f5f9' }}>
                        <img
                          src={url}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          onError={e => { e.target.style.display = 'none'; e.target.parentNode.appendChild(document.createElement('div')).setAttribute('style', 'width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:10px;color:#94a3b8'); e.target.parentNode.lastChild.textContent = meta.ext; }}
                        />
                      </div>
                    ) : (
                      <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}>
                        <FileTypeIcon kind={meta.kind} ext={meta.ext} size={56} />
                      </div>
                    )}
                    <p style={{ margin: 0, padding: '6px 8px', fontSize: 10, color: '#475569', textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.3 }}>
                      {displayName}
                    </p>
                  </div>
                </a>
                <button
                  type="button"
                  onClick={e => { e.preventDefault(); this.removeOldFile(index); }}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'rgba(239, 68, 68, 0.95)',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
                  }}
                  title={checkTranslation('{{lang}}Remove')}
                >
                  ×
                </button>
              </div>
            );
          })}

          {this.state.images.map((url, index) => {
            const file = Array.isArray(this.state.files) ? this.state.files[index] : (index === 0 ? this.state.files : null);
            const meta = getFileKind(file || {}, url);
            const displayName = (meta.name && meta.name.length > 30) ? meta.name.slice(0, 27) + '…' : (meta.name || 'file');
            return (
              <div
                key={`new-${index}`}
                style={{ position: 'relative', width: 100, flexShrink: 0 }}
              >
                <div
                  style={{
                    borderRadius: 12,
                    overflow: 'hidden',
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                  }}
                >
                  {meta.isImage && url ? (
                    <div style={{ width: 100, height: 100, background: '#f1f5f9' }}>
                      <img
                        src={url}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </div>
                  ) : (
                    <div style={{ padding: 12, display: 'flex', justifyContent: 'center' }}>
                      <FileTypeIcon kind={meta.kind} ext={meta.ext} size={56} />
                    </div>
                  )}
                  <p style={{ margin: 0, padding: '6px 8px', fontSize: 10, color: '#475569', textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.3 }}>
                    {displayName}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => this.removeFile('key', index)}
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'rgba(239, 68, 68, 0.95)',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.2)'
                  }}
                  title={checkTranslation('{{lang}}Remove')}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

export default File;
